import { useState, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { productsAPI } from '../utils/api';
import { ProdottoImmagine } from '../types';
import toast from 'react-hot-toast';
import {
  X,
  Loader2,
  Upload,
  Star,
  Trash2,
  Image,
  GripVertical,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  Type,
  Check,
} from 'lucide-react';

interface ProductImageGalleryProps {
  isOpen: boolean;
  onClose: () => void;
  prodottoId: string;
  prodottoNome: string;
}

const MAX_IMAGES = 10;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function ProductImageGallery({
  isOpen,
  onClose,
  prodottoId,
  prodottoNome,
}: ProductImageGalleryProps) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // State
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [editingAltText, setEditingAltText] = useState<string | null>(null);
  const [altTextValue, setAltTextValue] = useState('');
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [dragOverZone, setDragOverZone] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<Record<string, boolean>>({});

  // Drag reorder state
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  // Fetch images
  const { data: immagini = [], isLoading } = useQuery({
    queryKey: ['product-images', prodottoId],
    queryFn: async () => {
      const response = await productsAPI.getImmagini(prodottoId);
      return response.data;
    },
    enabled: isOpen,
  });

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async ({ file, isDefault }: { file: File; isDefault: boolean }) => {
      const response = await productsAPI.uploadImmagine(prodottoId, file, isDefault);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-images', prodottoId] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nel caricamento');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (immagineId: string) => productsAPI.deleteImmagine(prodottoId, immagineId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-images', prodottoId] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Immagine eliminata');
      setDeleteConfirmId(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
      setDeleteConfirmId(null);
    },
  });

  // Set default mutation
  const setDefaultMutation = useMutation({
    mutationFn: (immagineId: string) => productsAPI.setDefaultImmagine(prodottoId, immagineId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-images', prodottoId] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Immagine predefinita aggiornata');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore');
    },
  });

  // Reorder mutation
  const reorderMutation = useMutation({
    mutationFn: (ordini: { id: string; ordine: number }[]) =>
      productsAPI.reorderImmagini(prodottoId, ordini),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-images', prodottoId] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nel riordinamento');
    },
  });

  // Update alt text mutation
  const updateAltTextMutation = useMutation({
    mutationFn: ({ immagineId, altText }: { immagineId: string; altText: string }) =>
      productsAPI.updateImmagine(prodottoId, immagineId, { alt_text: altText }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-images', prodottoId] });
      setEditingAltText(null);
      toast.success('Testo alternativo aggiornato');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore');
    },
  });

  // File validation
  const validateFile = useCallback((file: File): string | null => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return `"${file.name}" non è un formato supportato. Usa JPEG, PNG, WebP o GIF.`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `"${file.name}" supera i 5 MB (${formatFileSize(file.size)}).`;
    }
    return null;
  }, []);

  // Handle file selection
  const handleFilesSelected = useCallback(
    (files: FileList | File[]) => {
      const fileArray = Array.from(files);
      const remainingSlots = MAX_IMAGES - immagini.length - pendingFiles.length;

      if (fileArray.length > remainingSlots) {
        toast.error(`Puoi aggiungere ancora ${remainingSlots} immagini (max ${MAX_IMAGES}).`);
        return;
      }

      const validFiles: File[] = [];
      for (const file of fileArray) {
        const error = validateFile(file);
        if (error) {
          toast.error(error);
        } else {
          validFiles.push(file);
        }
      }

      if (validFiles.length > 0) {
        setPendingFiles((prev) => [...prev, ...validFiles]);
      }
    },
    [immagini.length, pendingFiles.length, validateFile]
  );

  // Upload pending files
  const handleUpload = async () => {
    if (pendingFiles.length === 0) return;

    const progressMap: Record<string, boolean> = {};
    pendingFiles.forEach((f) => {
      progressMap[f.name] = true;
    });
    setUploadProgress(progressMap);

    let successCount = 0;
    for (const file of pendingFiles) {
      try {
        await uploadMutation.mutateAsync({
          file,
          isDefault: immagini.length === 0 && successCount === 0,
        });
        successCount++;
      } catch {
        // Error already handled in mutation onError
      }
      setUploadProgress((prev) => {
        const next = { ...prev };
        delete next[file.name];
        return next;
      });
    }

    setPendingFiles([]);
    setUploadProgress({});
    if (successCount > 0) {
      toast.success(
        successCount === 1
          ? 'Immagine caricata con successo'
          : `${successCount} immagini caricate con successo`
      );
    }
  };

  // Remove pending file
  const removePendingFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Drop zone handlers
  const handleDragOverZone = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverZone(true);
  };

  const handleDragLeaveZone = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverZone(false);
  };

  const handleDropZone = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverZone(false);
    if (e.dataTransfer.files.length > 0) {
      handleFilesSelected(e.dataTransfer.files);
    }
  };

  // Drag reorder handlers
  const handleDragStart = (e: React.DragEvent, id: string) => {
    setDraggedId(id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);
  };

  const handleDragOver = (e: React.DragEvent, id: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedId && id !== draggedId) {
      setDragOverId(id);
    }
  };

  const handleDragEnd = () => {
    setDraggedId(null);
    setDragOverId(null);
  };

  const handleDropReorder = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (!draggedId || draggedId === targetId) {
      setDraggedId(null);
      setDragOverId(null);
      return;
    }

    const items = [...immagini];
    const dragIndex = items.findIndex((img) => img.id === draggedId);
    const targetIndex = items.findIndex((img) => img.id === targetId);

    if (dragIndex === -1 || targetIndex === -1) return;

    const [removed] = items.splice(dragIndex, 1);
    items.splice(targetIndex, 0, removed);

    const ordini = items.map((img, i) => ({ id: img.id, ordine: i + 1 }));
    reorderMutation.mutate(ordini);

    setDraggedId(null);
    setDragOverId(null);
  };

  // Alt text editing
  const startEditAltText = (img: ProdottoImmagine) => {
    setEditingAltText(img.id);
    setAltTextValue(img.alt_text || '');
  };

  const saveAltText = (immagineId: string) => {
    updateAltTextMutation.mutate({ immagineId, altText: altTextValue });
  };

  // Lightbox navigation
  const openLightbox = (index: number) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);

  const navigateLightbox = (direction: -1 | 1) => {
    if (lightboxIndex === null) return;
    const newIndex = lightboxIndex + direction;
    if (newIndex >= 0 && newIndex < immagini.length) {
      setLightboxIndex(newIndex);
    }
  };

  const isUploading = Object.keys(uploadProgress).length > 0;

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex justify-between items-center p-6 border-b">
            <div>
              <h2 className="text-xl font-semibold">Immagini Prodotto</h2>
              <p className="text-sm text-gray-500 mt-1">
                {prodottoNome}
                <span className="ml-3 text-gray-400">
                  {immagini.length}/{MAX_IMAGES}
                </span>
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto p-6">
            {isLoading ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                <span className="ml-2 text-gray-600">Caricamento immagini...</span>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Image Grid */}
                {immagini.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Image className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>Nessuna immagine per questo prodotto</p>
                    <p className="text-sm mt-1">Trascina le immagini qui sotto per aggiungerle</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {immagini.map((img, index) => (
                      <div
                        key={img.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, img.id)}
                        onDragOver={(e) => handleDragOver(e, img.id)}
                        onDragEnd={handleDragEnd}
                        onDrop={(e) => handleDropReorder(e, img.id)}
                        className={`relative group rounded-lg border-2 overflow-hidden transition-all ${
                          draggedId === img.id
                            ? 'opacity-50 border-blue-400'
                            : dragOverId === img.id
                            ? 'border-blue-500 shadow-lg scale-105'
                            : img.is_default
                            ? 'border-yellow-400'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {/* Thumbnail */}
                        <div
                          className="aspect-square bg-gray-100 cursor-pointer relative"
                          onClick={() => openLightbox(index)}
                        >
                          {img.url ? (
                            <img
                              src={img.url}
                              alt={img.alt_text || img.filename}
                              className="w-full h-full object-contain"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Image className="h-8 w-8 text-gray-300" />
                            </div>
                          )}

                          {/* Overlay on hover */}
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                            <ZoomIn className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>

                          {/* Default badge */}
                          {img.is_default && (
                            <div className="absolute top-2 left-2 bg-yellow-400 text-yellow-900 rounded-full p-1">
                              <Star className="h-3 w-3" fill="currentColor" />
                            </div>
                          )}

                          {/* Drag handle */}
                          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing">
                            <div className="bg-white bg-opacity-80 rounded p-0.5">
                              <GripVertical className="h-4 w-4 text-gray-600" />
                            </div>
                          </div>
                        </div>

                        {/* Info + Actions */}
                        <div className="p-2">
                          <p className="text-xs text-gray-600 truncate" title={img.filename}>
                            {img.filename}
                          </p>
                          {img.file_size_bytes && (
                            <p className="text-xs text-gray-400">
                              {formatFileSize(img.file_size_bytes)}
                            </p>
                          )}

                          {/* Alt text */}
                          {editingAltText === img.id ? (
                            <div className="mt-1 flex gap-1">
                              <input
                                type="text"
                                value={altTextValue}
                                onChange={(e) => setAltTextValue(e.target.value)}
                                placeholder="Alt text..."
                                className="flex-1 text-xs px-1.5 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') saveAltText(img.id);
                                  if (e.key === 'Escape') setEditingAltText(null);
                                }}
                              />
                              <button
                                onClick={() => saveAltText(img.id)}
                                disabled={updateAltTextMutation.isPending}
                                className="text-green-600 hover:text-green-700"
                              >
                                <Check className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => setEditingAltText(null)}
                                className="text-gray-400 hover:text-gray-600"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => startEditAltText(img)}
                              className="mt-1 text-xs text-gray-400 hover:text-blue-600 flex items-center gap-1"
                            >
                              <Type className="h-3 w-3" />
                              {img.alt_text || 'Aggiungi alt text'}
                            </button>
                          )}

                          {/* Action buttons */}
                          <div className="mt-2 flex gap-1">
                            {!img.is_default && (
                              <button
                                onClick={() => setDefaultMutation.mutate(img.id)}
                                disabled={setDefaultMutation.isPending}
                                className="flex-1 text-xs px-2 py-1 bg-yellow-50 text-yellow-700 rounded hover:bg-yellow-100 flex items-center justify-center gap-1"
                                title="Imposta come predefinita"
                              >
                                <Star className="h-3 w-3" />
                                Predefinita
                              </button>
                            )}
                            <button
                              onClick={() => setDeleteConfirmId(img.id)}
                              className="flex-1 text-xs px-2 py-1 bg-red-50 text-red-700 rounded hover:bg-red-100 flex items-center justify-center gap-1"
                              title="Elimina immagine"
                            >
                              <Trash2 className="h-3 w-3" />
                              Elimina
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload Zone */}
                {immagini.length < MAX_IMAGES && (
                  <div className="border-t pt-6">
                    <h3 className="text-sm font-medium text-gray-700 mb-3">Carica immagini</h3>

                    {/* Drop zone */}
                    <div
                      onDragOver={handleDragOverZone}
                      onDragLeave={handleDragLeaveZone}
                      onDrop={handleDropZone}
                      onClick={() => fileInputRef.current?.click()}
                      className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                        dragOverZone
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                      }`}
                    >
                      <Upload
                        className={`h-8 w-8 mx-auto mb-2 ${
                          dragOverZone ? 'text-blue-500' : 'text-gray-400'
                        }`}
                      />
                      <p className="text-sm text-gray-600">
                        Trascina le immagini qui oppure{' '}
                        <span className="text-blue-600 font-medium">sfoglia</span>
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        JPEG, PNG, WebP, GIF - Max 5 MB per file
                      </p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept={ACCEPTED_TYPES.join(',')}
                        onChange={(e) => {
                          if (e.target.files) handleFilesSelected(e.target.files);
                          e.target.value = '';
                        }}
                        className="hidden"
                      />
                    </div>

                    {/* Pending files preview */}
                    {pendingFiles.length > 0 && (
                      <div className="mt-4 space-y-2">
                        <p className="text-sm font-medium text-gray-700">
                          File selezionati ({pendingFiles.length})
                        </p>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          {pendingFiles.map((file, i) => (
                            <div
                              key={`${file.name}-${i}`}
                              className="relative rounded-lg border border-gray-200 overflow-hidden"
                            >
                              <div className="aspect-square bg-gray-100">
                                <img
                                  src={URL.createObjectURL(file)}
                                  alt={file.name}
                                  className="w-full h-full object-contain"
                                />
                              </div>
                              <div className="p-1.5">
                                <p className="text-xs text-gray-600 truncate">{file.name}</p>
                                <p className="text-xs text-gray-400">
                                  {formatFileSize(file.size)}
                                </p>
                              </div>
                              {uploadProgress[file.name] ? (
                                <div className="absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center">
                                  <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                                </div>
                              ) : (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removePendingFile(i);
                                  }}
                                  className="absolute top-1 right-1 bg-white rounded-full p-0.5 shadow hover:bg-red-50"
                                >
                                  <X className="h-4 w-4 text-red-500" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-end gap-2 mt-3">
                          <button
                            onClick={() => setPendingFiles([])}
                            disabled={isUploading}
                            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                          >
                            Annulla
                          </button>
                          <button
                            onClick={handleUpload}
                            disabled={isUploading}
                            className="flex items-center gap-2 px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                          >
                            {isUploading ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Upload className="h-4 w-4" />
                            )}
                            Carica {pendingFiles.length > 1 ? `(${pendingFiles.length})` : ''}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
            >
              Chiudi
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation */}
      {deleteConfirmId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Conferma Eliminazione</h3>
            <p className="text-gray-600 mb-6">
              Sei sicuro di voler eliminare questa immagine? L'operazione non puo essere annullata.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annulla
              </button>
              <button
                onClick={() => deleteMutation.mutate(deleteConfirmId)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  'Elimina'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxIndex !== null && immagini[lightboxIndex] && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60]"
          onClick={closeLightbox}
        >
          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-gray-300"
          >
            <X className="h-8 w-8" />
          </button>

          {/* Navigation - Previous */}
          {lightboxIndex > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigateLightbox(-1);
              }}
              className="absolute left-4 text-white hover:text-gray-300"
            >
              <ChevronLeft className="h-10 w-10" />
            </button>
          )}

          {/* Image */}
          <div
            className="max-w-[90vw] max-h-[85vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={immagini[lightboxIndex].url}
              alt={immagini[lightboxIndex].alt_text || immagini[lightboxIndex].filename}
              className="max-w-full max-h-[85vh] object-contain rounded"
            />
          </div>

          {/* Navigation - Next */}
          {lightboxIndex < immagini.length - 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigateLightbox(1);
              }}
              className="absolute right-4 text-white hover:text-gray-300"
            >
              <ChevronRight className="h-10 w-10" />
            </button>
          )}

          {/* Image info bar */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black bg-opacity-60 text-white rounded-lg px-4 py-2 text-sm">
            {immagini[lightboxIndex].filename}
            <span className="ml-3 text-gray-300">
              {lightboxIndex + 1} / {immagini.length}
            </span>
          </div>
        </div>
      )}
    </>
  );
}
